import { useEffect, useState, useCallback } from 'react';
import {
  Box, Stack, Typography, Input, Button, Select, Option,
  Chip, IconButton, Table, Sheet, Divider, Tooltip,
  Modal, ModalOverflow, ModalDialog, ModalClose, FormControl, FormLabel, Textarea,
  Card,
} from '@mui/joy';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import {
  APIFinanceOperations, APIFinanceOperationCreate, APIFinanceOperationUpdate, APIFinanceOperationDelete,
  APIFinanceCashflow, APIFinanceCategories, APIFinanceCategoryCreate, APIFinanceAccounts, APIFinanceAccountCreate,
} from '../../../api';
import {
  useFinanceToken, useIsMobile, PeriodBar, MetricCard, MetricGrid, SectionCard, scrollTableSx,
  fmtMoney, todayISO, monthStartISO,
} from './financeShared';



const SOURCE_LABELS = {
  manual: 'Вручную', tochka_import: 'Импорт Точки', client_payment: 'Оплата клиента', payout: 'Выплата',
};
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('ru-RU') : '');

export default function FinanceCashflowTab() {
  const token = useFinanceToken();
  const isMobile = useIsMobile();
  const [dateFrom, setDateFrom] = useState(monthStartISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [direction, setDirection] = useState('');
  const [accountId, setAccountId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [source, setSource] = useState('');
  const [page, setPage] = useState(1);

  const [cf, setCf] = useState(null);
  const [ops, setOps] = useState({ results: [], pagination: {} });
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [opModal, setOpModal] = useState({ open: false, op: null });
  const [dictOpen, setDictOpen] = useState(false);

  const loadDicts = useCallback(() => {
    if (!token) return;
    APIFinanceCategories({}, token).then((d) => setCategories(d || [])).catch(() => {});
    APIFinanceAccounts({}, token).then((d) => setAccounts(d || [])).catch(() => {});
  }, [token]);
  useEffect(() => { loadDicts(); }, [loadDicts]);

  const loadCashflow = useCallback(() => {
    if (!token) return;
    APIFinanceCashflow({ date_from: dateFrom, date_to: dateTo }, token).then(setCf).catch(() => {});
  }, [token, dateFrom, dateTo]);
  useEffect(() => { loadCashflow(); }, [loadCashflow]);

  const loadOps = useCallback(() => {
    if (!token) return;
    setLoading(true);
    APIFinanceOperations({
      date_from: dateFrom, date_to: dateTo,
      direction: direction || undefined,
      account_id: accountId || undefined,
      category_id: categoryId || undefined,
      source: source || undefined,
      page,
    }, token)
      .then((d) => setOps(d || { results: [], pagination: {} }))
      .catch((e) => console.error('ops load', e))
      .finally(() => setLoading(false));
  }, [token, dateFrom, dateTo, direction, accountId, categoryId, source, page]);
  useEffect(() => { loadOps(); }, [loadOps]);

  // сброс страницы при смене фильтров/периода
  useEffect(() => { setPage(1); }, [dateFrom, dateTo, direction, accountId, categoryId, source]);

  const refreshAll = () => { loadOps(); loadCashflow(); };

  const chartData = (cf?.labels || []).map((label, i) => ({
    label, in: cf.in?.[i] ?? 0, out: cf.out?.[i] ?? 0, balance: cf.balance?.[i] ?? 0,
  }));

  return (
    <Box>
      <PeriodBar
        from={dateFrom} to={dateTo}
        onFrom={setDateFrom} onTo={setDateTo}
        onPreset={(f, t) => { setDateFrom(f); setDateTo(t); }}
        loading={loading}
        right={
          <>
            <Button size="sm" variant="outlined" color="neutral" onClick={() => setDictOpen(true)} sx={{ borderRadius: 'lg' }}>
              Справочники
            </Button>
            <Button size="sm" startDecorator={<AddRoundedIcon />} onClick={() => setOpModal({ open: true, op: null })} sx={{ borderRadius: 'lg' }}>
              Операция
            </Button>
          </>
        }
      />

      {/* Итоги */}
      <MetricGrid>
        <MetricCard index={0} title="Приход" value={fmtMoney(cf?.totals?.in)}  color="#059669" />
        <MetricCard index={1} title="Расход" value={fmtMoney(cf?.totals?.out)} color="#f59e0b" />
        <MetricCard index={2} title="Сальдо" value={fmtMoney(cf?.totals?.net)} color="#6366f1" />
      </MetricGrid>

      {/* График ДДС */}
      <SectionCard title="Движение денег по дням">
        <Box sx={{ height: { xs: 260, md: 300 }, mx: { xs: -0.5, md: 0 } }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 12, left: isMobile ? -18 : 0, bottom: 0 }}>
              <defs>
                <linearGradient id="cfIn" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#059669" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#059669" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="cfOut" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="cfBal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.30} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={16} />
              <YAxis tick={{ fontSize: 11 }} width={isMobile ? 46 : 64} tickFormatter={(v) => Number(v).toLocaleString('ru-RU')} />
              <RTooltip formatter={(v) => fmtMoney(v)} contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }} />
              <Legend />
              <Area type="monotone" dataKey="in"      name="Приход" stroke="#059669" strokeWidth={2} fill="url(#cfIn)" />
              <Area type="monotone" dataKey="out"     name="Расход" stroke="#f59e0b" strokeWidth={2} fill="url(#cfOut)" />
              <Area type="monotone" dataKey="balance" name="Сальдо" stroke="#6366f1" strokeWidth={2} fill="url(#cfBal)" />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </SectionCard>

      {/* Разбивка по статьям */}
      {cf?.by_category?.length > 0 && (
        <SectionCard title="По статьям">
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {cf.by_category.map((c, i) => (
              <Chip key={i} variant="soft" color={c.direction === 'in' ? 'success' : 'warning'} sx={{ borderRadius: 'xl', py: 0.5 }}>
                {c.name}: {fmtMoney(c.total)}
              </Chip>
            ))}
          </Box>
        </SectionCard>
      )}

      {/* Журнал операций */}
      <SectionCard title="Журнал операций">
        {/* Фильтры */}
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Select size="sm" value={direction} onChange={(_, v) => setDirection(v ?? '')} sx={{ minWidth: 140 }}>
            <Option value="">Все типы</Option>
            <Option value="in">Приход</Option>
            <Option value="out">Расход</Option>
          </Select>
          <Select size="sm" value={accountId} onChange={(_, v) => setAccountId(v ?? '')} sx={{ minWidth: 150 }}>
            <Option value="">Все счета</Option>
            {accounts.map((a) => <Option key={a.id} value={a.id}>{a.name}</Option>)}
          </Select>
          <Select size="sm" value={categoryId} onChange={(_, v) => setCategoryId(v ?? '')} sx={{ minWidth: 170 }}>
            <Option value="">Все статьи</Option>
            {categories.map((c) => <Option key={c.id} value={c.id}>{c.name}</Option>)}
          </Select>
          <Select size="sm" value={source} onChange={(_, v) => setSource(v ?? '')} sx={{ minWidth: 150 }}>
            <Option value="">Все источники</Option>
            {Object.entries(SOURCE_LABELS).map(([k, v]) => <Option key={k} value={k}>{v}</Option>)}
          </Select>
        </Stack>

        <Sheet variant="outlined" sx={{ borderRadius: 'lg', overflow: 'auto' }}>
          <Table borderAxis="both" stickyHeader hoverRow sx={scrollTableSx(920)}>
            <thead>
              <tr>
                <th style={{ width: 110 }}>Дата</th>
                <th style={{ width: 90 }}>Тип</th>
                <th style={{ width: 130, textAlign: 'right' }}>Сумма</th>
                <th style={{ width: 180 }}>Статья</th>
                <th style={{ width: 150 }}>Счёт</th>
                <th style={{ width: 130 }}>Источник</th>
                <th>Комментарий</th>
                <th style={{ width: 90, textAlign: 'center' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {(ops.results || []).map((op) => (
                <tr key={op.id}>
                  <td>{fmtDate(op.operation_date)}</td>
                  <td>
                    <Chip size="sm" variant="soft" color={op.direction === 'in' ? 'success' : 'warning'}>
                      {op.direction === 'in' ? 'Приход' : 'Расход'}
                    </Chip>
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 600, color: op.direction === 'in' ? '#059669' : '#b45309' }}>
                    {op.direction === 'in' ? '+' : '−'}{fmtMoney(op.amount)}
                  </td>
                  <td>
                    {(op.source === 'manual' || op.source === 'tochka_import') ? (
                      <Select size="sm" value={op.category ?? null} placeholder="Без статьи"
                        onChange={(_, v) => {
                          APIFinanceOperationUpdate(op.id, { category: v ?? null }, token)
                            .then(refreshAll)
                            .catch((e) => console.error('set category', e));
                        }}
                        sx={{ minWidth: 150 }}>
                        <Option value={null}>Без статьи</Option>
                        {categories.map((c) => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                      </Select>
                    ) : (op.category_name || '—')}
                  </td>
                  <td>{op.account_name || '—'}</td>
                  <td>{op.source_display || SOURCE_LABELS[op.source] || op.source}</td>
                  <td>{op.comment || op.counterparty || '—'}</td>
                  <td style={{ textAlign: 'center' }}>
                    {op.source === 'manual' ? (
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <IconButton size="sm" variant="plain" onClick={() => setOpModal({ open: true, op })}>
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="sm" variant="plain" color="danger"
                          onClick={() => {
                            if (window.confirm('Удалить операцию?')) {
                              APIFinanceOperationDelete(op.id, token).then(refreshAll).catch((e) => console.error(e));
                            }
                          }}>
                          <DeleteRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    ) : (
                      <Tooltip title="Авто-операция — не редактируется"><span>—</span></Tooltip>
                    )}
                  </td>
                </tr>
              ))}
              {(!ops.results || ops.results.length === 0) && (
                <tr><td colSpan={8} style={{ textAlign: 'center', color: '#94a3b8' }}>Нет операций за период</td></tr>
              )}
            </tbody>
          </Table>
        </Sheet>

        {/* Пагинация */}
        {ops.pagination?.total_pages > 1 && (
          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 1.5 }}>
            <Button size="sm" variant="outlined" disabled={!ops.pagination.previous} onClick={() => setPage((p) => Math.max(1, p - 1))}>Назад</Button>
            <Typography level="body-sm">{ops.pagination.current_page} / {ops.pagination.total_pages}</Typography>
            <Button size="sm" variant="outlined" disabled={!ops.pagination.next} onClick={() => setPage((p) => p + 1)}>Вперёд</Button>
          </Stack>
        )}
      </SectionCard>
    </Box>
  );
}


// ── Модалка операции (добавление/редактирование) ──
function OperationModal({ state, token, categories, accounts, onClose, onSaved }) {
  const editing = !!state.op;
  const [direction, setDirection] = useState('out');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState('');
  const [accountId, setAccountId] = useState(null);
  const [categoryId, setCategoryId] = useState(null);
  const [counterparty, setCounterparty] = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!state.open) return;
    const op = state.op;
    setDirection(op?.direction || 'out');
    setAmount(op?.amount ?? '');
    setDate(op?.operation_date ? op.operation_date.slice(0, 16) : new Date().toISOString().slice(0, 16));
    setAccountId(op?.account ?? null);
    setCategoryId(op?.category ?? null);
    setCounterparty(op?.counterparty ?? '');
    setComment(op?.comment ?? '');
  }, [state.open, state.op]);

  const catOptions = categories.filter((c) => c.direction === direction);

  const save = () => {
    if (!amount || !date) { window.alert('Заполните сумму и дату'); return; }
    setSaving(true);
    const payload = {
      direction, amount: Number(amount), operation_date: date,
      account: accountId || null, category: categoryId || null,
      counterparty, comment,
    };
    const req = editing
      ? APIFinanceOperationUpdate(state.op.id, payload, token)
      : APIFinanceOperationCreate(payload, token);
    req.then(() => onSaved()).catch((e) => console.error('op save', e)).finally(() => setSaving(false));
  };

  return (
    <Modal open={state.open} onClose={onClose}>
      <ModalOverflow>
        <ModalDialog sx={{ minWidth: 360, maxWidth: 460 }}>
          <ModalClose onClick={onClose} />
          <Typography level="title-md" sx={{ mb: 1 }}>{editing ? 'Редактировать операцию' : 'Новая операция'}</Typography>
          <Stack spacing={1.2}>
            <FormControl>
              <FormLabel>Направление</FormLabel>
              <Select value={direction} onChange={(_, v) => { if (v) { setDirection(v); setCategoryId(null); } }}>
                <Option value="out">Расход</Option>
                <Option value="in">Приход</Option>
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Сумма, ₽</FormLabel>
                <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </FormControl>
              <FormControl sx={{ flex: 1.3 }}>
                <FormLabel>Дата и время</FormLabel>
                <Input type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} />
              </FormControl>
            </Stack>
            <FormControl>
              <FormLabel>Статья</FormLabel>
              <Select value={categoryId} onChange={(_, v) => setCategoryId(v)} placeholder="Без статьи">
                <Option value={null}>Без статьи</Option>
                {catOptions.map((c) => <Option key={c.id} value={c.id}>{c.name}</Option>)}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Счёт / касса</FormLabel>
              <Select value={accountId} onChange={(_, v) => setAccountId(v)} placeholder="Без счёта">
                <Option value={null}>Без счёта</Option>
                {accounts.map((a) => <Option key={a.id} value={a.id}>{a.name}</Option>)}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Контрагент</FormLabel>
              <Input value={counterparty} onChange={(e) => setCounterparty(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel>Комментарий</FormLabel>
              <Textarea minRows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
            </FormControl>
            <Button onClick={save} loading={saving} sx={{ mt: 0.5 }}>Сохранить</Button>
          </Stack>
        </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
}

// ── Модалка справочников (просмотр + добавление статей и счетов) ──
function DictionariesModal({ open, token, categories, accounts, onClose, onChanged }) {
  const [catName, setCatName] = useState('');
  const [catDir, setCatDir] = useState('out');
  const [accName, setAccName] = useState('');
  const [accType, setAccType] = useState('other');
  const [busy, setBusy] = useState(false);

  const addCategory = () => {
    if (!catName.trim()) return;
    setBusy(true);
    APIFinanceCategoryCreate({ name: catName.trim(), direction: catDir, flow_type: 'operating', include_in_pnl: true }, token)
      .then(() => { setCatName(''); onChanged(); })
      .catch((e) => console.error(e)).finally(() => setBusy(false));
  };
  const addAccount = () => {
    if (!accName.trim()) return;
    setBusy(true);
    APIFinanceAccountCreate({ name: accName.trim(), account_type: accType }, token)
      .then(() => { setAccName(''); onChanged(); })
      .catch((e) => console.error(e)).finally(() => setBusy(false));
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalOverflow>
        <ModalDialog sx={{ minWidth: 380, maxWidth: 520 }}>
          <ModalClose onClick={onClose} />
          <Typography level="title-md" sx={{ mb: 1 }}>Справочники</Typography>

          <Typography level="title-sm" sx={{ mt: 1 }}>Статьи операций</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
            {categories.map((c) => (
              <Chip key={c.id} size="sm" variant="soft" color={c.direction === 'in' ? 'success' : 'warning'}>{c.name}</Chip>
            ))}
          </Box>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Input size="sm" placeholder="Название статьи" value={catName} onChange={(e) => setCatName(e.target.value)} sx={{ flex: 1 }} />
            <Select size="sm" value={catDir} onChange={(_, v) => v && setCatDir(v)} sx={{ width: 120 }}>
              <Option value="out">Расход</Option>
              <Option value="in">Приход</Option>
            </Select>
            <Button size="sm" onClick={addCategory} loading={busy}>Добавить</Button>
          </Stack>

          <Divider />

          <Typography level="title-sm" sx={{ mt: 1.5 }}>Счета / кассы</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
            {accounts.map((a) => <Chip key={a.id} size="sm" variant="soft">{a.name}</Chip>)}
          </Box>
          <Stack direction="row" spacing={1}>
            <Input size="sm" placeholder="Название счёта" value={accName} onChange={(e) => setAccName(e.target.value)} sx={{ flex: 1 }} />
            <Select size="sm" value={accType} onChange={(_, v) => v && setAccType(v)} sx={{ width: 150 }}>
              <Option value="tochka">Точка</Option>
              <Option value="cash">Наличные</Option>
              <Option value="personal_card">Личная карта</Option>
              <Option value="other">Прочее</Option>
            </Select>
            <Button size="sm" onClick={addAccount} loading={busy}>Добавить</Button>
          </Stack>
        </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
}